import React, { useState, useEffect } from 'react';
import { string, shape, number } from 'prop-types';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatCurrencyMajorUnit } from '../../util/currency';
import { useConfiguration } from '../../context/configurationContext';
import {
  AvatarSmall,
  NamedLink,
  AspectRatioWrapper,
  IconSpinner,
  ReviewRating,
  EventCard,
} from '../../components';
import { createSlug } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import { addToFavorites } from '../../containers/LandingPage/LandingPage.duck';

import css from './SessionTypeCard.module.css';

const SessionTypeCard = (props) => {
  const { className, rootClassName, sessionType } = props;
  const config = useConfiguration();
  const intl = useIntl();
  const dispatch = useDispatch();

  const [isFavorite, setIsFavorite] = useState(false);
  const [loader, setLoader] = useState(false);
  const currentUser = useSelector((state) => state.user.currentUser);

  const classes = classNames(rootClassName || css.root, className);

  const { _id, listingId, sessionName, category, sessionImage, price, author } = sessionType;
  const slug = createSlug(sessionName);
  const formattedPrice = formatCurrencyMajorUnit(intl, config.currency, price);
  const { aspectWidth = 1, aspectHeight = 1 } = config.layout.listingImage;
  const coachName = userDisplayNameAsString(author, '');

  const rating =
    sessionType.rating !== undefined
      ? sessionType.rating
      : sessionType.author?.rating ||
      sessionType.author?.attributes?.profile?.publicData?.rating ||
      0;
  const reviewCount =
    sessionType.reviewCount !== undefined
      ? sessionType.reviewCount
      : sessionType.author?.reviewCount ||
      sessionType.author?.attributes?.profile?.publicData?.reviewCount ||
      0;

  const hasEventData = true; // Always use EventCard design for SearchPage sessions

  useEffect(() => {
    const favoritesListing = currentUser?.attributes?.profile?.publicData?.favoritesListing || [];
    const isListingAlreadyExist = favoritesListing.includes(listingId);
    setIsFavorite(isListingAlreadyExist);
  }, [currentUser, listingId]);

  const handleFavToggle = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!currentUser) return;

    setLoader(true);
    try {
      const res = await dispatch(
        addToFavorites({
          listingId: listingId,
          currentUserId: currentUser?.id?.uuid,
          type: 'listing',
        })
      );
      if (res) setIsFavorite(res.isFavorites);
    } catch (e) {
      console.error('Error toggling favorite:', e);
    } finally {
      setLoader(false);
    }
  };

  if (hasEventData) {
    console.log("author", author)
    const eventData = {
      title: sessionName,
      sessionName: sessionName,
      image: sessionImage,
      sessionImage: sessionImage,
      price: price,
      // EventCard reads event.mainCategory/event.subCategory for its badge
      // (categoryToAccentVar etc.) -- `category` here is the unrelated
      // "Sessiontype" enum (coaching/training/consulting/workshop), passing
      // it as `category` left EventCard's `event.mainCategory` always
      // undefined, so the category/subcategory badge was blank on every
      // session card sitewide regardless of what the coach actually set.
      mainCategory: sessionType.mainCategory,
      subCategory: sessionType.subCategory,
      nextDate: sessionType.nextDate,
      startTime: sessionType.startTime,
      durationHours: sessionType.durationHours,
      durationLabel: sessionType.durationLabel,
      location: sessionType.location,
      address: sessionType.address,
      listingId: listingId,
      sessionTypeId: _id,
      author: author,
      host: {
        id: author?.id,
        displayName: coachName,
        profileImage: { ...author?.profileImage, url: author?.profileImage?.attributes?.variants?.['square-small']?.url },
      },
      rating: rating,
      reviewCount: reviewCount,
      // sessionType docs (search/favorites) carry `isCampSession`, not `isCamp`
      // (that field only exists on LandingPageSessionsController.js's own
      // response shape) -- reading isCamp here always fell through to
      // 'In-Person', so camp session types never showed the Camp label.
      sessionType: sessionType.sessionType || (sessionType.isCampSession ? 'Camp' : 'In-Person'),
      isCamp: sessionType.isCampSession,
      spotsLeft: sessionType.spotsLeft,
      totalSpots: sessionType.totalSpots,
    };

    return (
      <div className={classes}>
        <EventCard event={eventData} size="small" />
      </div>
    );
  }

  return (
    <NamedLink className={classes} name="CustomListingPage" params={{ id: listingId, slug }}>
      <AspectRatioWrapper className={css.imageWrapper} width={aspectWidth} height={aspectHeight}>
        <img src={sessionImage} alt={sessionName} className={css.rootForImage} />
        <button
          className={classNames(css.favoriteButton, { [css.favoriteActive]: isFavorite })}
          onClick={handleFavToggle}
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          {loader ? (
            <IconSpinner className={css.spinnerCustom} />
          ) : (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill={isFavorite ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          )}
        </button>
      </AspectRatioWrapper>

      <div className={css.info}>
        <div className={css.coachInfo}>
          <AvatarSmall user={author} className={css.coachAvatar} disableProfileLink />
          <span className={css.coachName}>{coachName}</span>
        </div>

        <h3 className={css.title}>{sessionName}</h3>
        <div className={css.category}>{category}</div>

        <div className={css.footer}>
          <div className={css.price}>{formattedPrice}</div>
          <div className={css.rating}>
            <ReviewRating
              rootClassName={css.reviewRating}
              rating={rating}
              reviewCount={reviewCount}
            />
          </div>
        </div>
      </div>
    </NamedLink>
  );
};

export default SessionTypeCard;
